// Listener de alertas sonoros. Traduz eventos de WS em bipes:
//   - Som A (mensagem): `inbox/message.received` INBOUND de uma conversa MINHA
//     (mesmo predicado do badge de não-lidas — inclui a conversa aberta na tela).
//   - Som B (fila): `inbox/conversation.status.changed` OU `inbox/conversation.created`
//     com status QUEUE (lead novo/handoff caindo na fila).
//
// SRP: só decide QUANDO tocar; o COMO (síntese/throttle) vive em lib/sound/alerts.
// Setup: registrado uma única vez no boot via attachNotificationSoundListener().
import type { AnyAction } from '@reduxjs/toolkit';
import { listenerMiddleware } from '@/app/store/middleware';
import { selectCurrentUser } from '@/features/auth';
import type { ConversationStatus, InboxMessage } from '@/features/inbox';
import { playMessageAlert, playQueueAlert } from '@/lib/sound/alerts';

interface MessageReceivedPayload {
  conversationId: string;
  message: InboxMessage;
  assignedToId: string | null;
}

interface ConversationStatusPayload {
  conversationId: string;
  status: ConversationStatus;
}

/**
 * Registra os listeners de som. Chamar 1× no boot (AppProviders).
 */
export function attachNotificationSoundListener(): void {
  // Som A — mensagem nova do cliente numa conversa minha (inclui a aberta).
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/message.received',
    effect: (action, listenerApi) => {
      const payload = (action as AnyAction & { payload?: MessageReceivedPayload }).payload;
      if (!payload?.message) return;
      if (payload.message.direction !== 'INBOUND') return;

      const state = listenerApi.getState() as Parameters<typeof selectCurrentUser>[0];
      const myId = selectCurrentUser(state)?.id ?? null;
      if (!myId || payload.assignedToId !== myId) return;

      playMessageAlert();
    },
  });

  // Som B — conversa entrou na fila (status QUEUE), seja mudança ou criação.
  listenerMiddleware.startListening({
    predicate: (action) => {
      const t = (action as AnyAction).type;
      return (
        t === 'inbox/conversation.status.changed' || t === 'inbox/conversation.created'
      );
    },
    effect: (action) => {
      const payload = (action as AnyAction & { payload?: ConversationStatusPayload }).payload;
      if (payload?.status !== 'QUEUE') return;
      playQueueAlert();
    },
  });
}
